import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Hero = ({ onBookClick }) => {
  const scrollToPortfolio = () => {
    const element = document.querySelector('#portfolio');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/photo-20.jpg"
          alt="Wedding Photography"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/40 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[#0a0a0a]/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[#d4af37] text-sm md:text-base tracking-[0.3em] uppercase font-light"
            data-testid="hero-tagline"
          >
            Premium Photography Studio
          </motion.p>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="font-['Playfair_Display'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium text-[#e5e5e5] leading-none tracking-tight"
            data-testid="hero-heading"
          >
            Capturing the
            <br />
            <span className="italic text-[#d4af37]">Soul</span> of the Moment
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-[#a3a3a3] text-base md:text-lg max-w-xl mx-auto leading-relaxed font-light"
            data-testid="hero-subheading"
          >
            Timeless wedding, portrait, and event photography with cinematic elegance.
            Every frame tells your unique story.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <Button
              onClick={scrollToPortfolio}
              className="bg-[#d4af37] text-[#0a0a0a] hover:bg-[#b3922d] rounded-none px-8 py-6 tracking-widest uppercase text-xs font-semibold w-full sm:w-auto"
              data-testid="hero-view-portfolio-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              View Portfolio
            </Button>
            <Button
              onClick={onBookClick}
              variant="outline"
              className="border-[#e5e5e5]/30 text-[#e5e5e5] hover:bg-[#e5e5e5]/10 hover:border-[#e5e5e5]/50 rounded-none px-8 py-6 tracking-widest uppercase text-xs font-medium w-full sm:w-auto"
              data-testid="hero-book-session-btn"
            >
              Book a Session
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2 cursor-pointer"
          onClick={() => document.querySelector('#about').scrollIntoView({ behavior: 'smooth' })}
          data-testid="scroll-indicator"
        >
          <span className="text-[#a3a3a3] text-xs tracking-widest uppercase">Scroll</span>
          <ChevronDown className="w-5 h-5 text-[#d4af37]" />
        </motion.div>
      </motion.div>

      {/* Side Stats */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-8 text-right z-10"
      >
        <div>
          <p className="text-[#d4af37] text-3xl font-['Playfair_Display']">500+</p>
          <p className="text-[#a3a3a3] text-xs tracking-widest uppercase">Events Covered</p>
        </div>
        <div className="w-px h-12 bg-[#d4af37]/30 self-end" />
        <div>
          <p className="text-[#d4af37] text-3xl font-['Playfair_Display']">10+</p>
          <p className="text-[#a3a3a3] text-xs tracking-widest uppercase">Years Experience</p>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
